import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCol,
  MDBIcon,
  MDBRow,
  MDBTable,
} from "mdbreact";
import { Services, Templates } from "../../../../../services/fakeDb";
import { GenericProducts } from "../../../../../constants";
import { axioKit } from "../../../../../services/utilities";
import { isUsableIn } from "../../../manager/commerce/merchandise/productGenerics/phases";
import { autoSelect, isComplete } from "../brandedProducts/select";
import ServiceList from "./setup/serviceList";
import SupplyPicker from "./setup/supplyPicker";
import SupplyRow from "./setup/supplyRow";
import { STATUS, newBrandsOf, signature, statusOf } from "./setup/status";

export const UTILIZATION_URL = "procurements/catalogs/service/utilization";

// fakeDb department -> serviceUtilization department
export const UTILIZATION_DEPARTMENT = {
  LAB: "LABORATORY",
  RAD: "RADIOLOGY",
};

// serviceUtilization phases -> page labels and sidebar route segment
export const UTILIZATION_PHASES = {
  preAnalytical: {
    title: "Pre-Analytical",
    abbr: "PRE",
    icon: "syringe",
    label: "pre-analytical supplies (syringe, tubes, cotton, etc.)",
    segment: "preanalytical",
  },
  analytical: {
    title: "Analytical",
    abbr: "ANA",
    icon: "flask",
    label: "analytical supplies (reagents, cartridges, etc.)",
    segment: "analytical",
  },
  postAnalytical: {
    title: "Post-Analytical",
    abbr: "POST",
    icon: "print",
    label: "post-analytical supplies (envelope, result folder, etc.)",
    segment: "postanalytical",
  },
};

/**
 * Consumables tagging of the pre-analytical phase.
 * Used by onboarding to auto-consume the supplies of the patient.
 */
export default function PreAnalyticalSetup({ readOnly = false }) {
  return <UtilizationSetup phase="preAnalytical" readOnly={readOnly} />;
}

// saved { pgid, product, variant, quantity } -> { generic, product, variant, quantity },
// skipping unknown/old entries. A tag saved without a brand stays without one, so the
// page can tell that a brand became available.
export const toItems = (list = [], products = []) =>
  list
    .map(({ pgid, product, variant, quantity }) => ({
      generic: GenericProducts.find(pgid),
      ...(product
        ? autoSelect(products, pgid, { product, variant })
        : { product: null, variant: null }),
      quantity,
    }))
    .filter(({ generic }) => generic);

// last save of the tagging of a service, used to detect brands added after it
const taggedAtOf = (utilizations, serviceId) =>
  utilizations.find((u) => u.serviceId === serviceId)?.updatedAt;

/**
 * Consumables tagging: service -> generic supplies -> default brand/variant + units.
 * The defaults are auto-consumed upon dispensing (e.g. CBC -> Syringe -> Terumo 3cc x1);
 * staff may change them there, which makes it a manual declaration.
 */
// readOnly: shows the tagging only (e.g. the frontdesk), nothing can be changed
export function UtilizationSetup({ phase = "preAnalytical", readOnly = false }) {
  const { title, abbr, icon, label } = UTILIZATION_PHASES[phase];
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    history = useHistory(),
    { pathname, search: query } = useLocation(),
    // ?department=LAB&service=61 when opened from the onboarding
    params = useMemo(() => new URLSearchParams(query), [query]),
    [department, setDepartment] = useState(
      ["LAB", "RAD"].includes(params.get("department"))
        ? params.get("department")
        : activePlatform?.department === "Radiology"
          ? "RAD"
          : "LAB",
    ),
    [products, setProducts] = useState([]),
    [utilizations, setUtilizations] = useState([]),
    [selected, setSelected] = useState(null),
    [items, setItems] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const services = useMemo(
    () =>
      Services.collections
        .filter(({ department: d }) => d === department)
        .map((service) => ({
          ...service,
          section:
            Templates.getComponentName(service.template, department) ||
            "Others",
        })),
    [department],
  );

  const getConfigured = useCallback(
    (serviceId, _utils = utilizations, _products = products) =>
      toItems(
        _utils.find((u) => u.serviceId === serviceId)?.phases?.[phase],
        _products,
      ),
    [utilizations, products, phase],
  );

  useEffect(() => {
    if (!branch) return;
    setIsLoading(true);
    setSelected(null);
    setItems([]);
    Promise.all([
      axioKit.universal("commerce/merchandise/products/browse", token, {
        branch,
      }),
      axioKit.universal(`${UTILIZATION_URL}/browse`, token, {
        branch,
        department: UTILIZATION_DEPARTMENT[department],
      }),
    ])
      .then(([_products, _utilizations]) => {
        const _brands = _products?.payload || [];
        const _utils = _utilizations?.payload || [];
        setProducts(_brands);
        setUtilizations(_utils);

        const service = Services.find(params.get("service"));
        if (service?.department === department) {
          setSelected({
            ...service,
            section:
              Templates.getComponentName(service.template, department) ||
              "Others",
          });
          setItems(getConfigured(service.id, _utils, _brands));
        }
      })
      .catch((error) =>
        addToast(error.message, { appearance: "error", autoDismiss: true }),
      )
      .finally(() => setIsLoading(false));
    // getConfigured changes with the fetched data, load only on these
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, department, token, addToast, params, phase]);

  // status of every service, from what is saved
  const statuses = useMemo(
    () =>
      services.reduce((result, { id }) => {
        result[id] = statusOf(
          getConfigured(id),
          products,
          taggedAtOf(utilizations, id),
        );
        return result;
      }, {}),
    [services, getConfigured, products, utilizations],
  );
  const serviceStatus = useCallback(
    (id) => statuses[id] || "notSet",
    [statuses],
  );

  const counts = useMemo(
    () =>
      Object.values(statuses).reduce(
        (result, status) => ({
          ...result,
          [status]: (result[status] || 0) + 1,
        }),
        {},
      ),
    [statuses],
  );

  const saved = useMemo(
    () => (selected ? getConfigured(selected.id) : []),
    [selected, getConfigured],
  );
  // compared with what is stored, so filled-in defaults of older tags can be saved
  const stored = useMemo(
    () =>
      (selected
        ? utilizations.find((u) => u.serviceId === selected.id)?.phases?.[
            phase
          ] || []
        : []
      ).filter(({ pgid }) => GenericProducts.find(pgid)),
    [selected, utilizations, phase],
  );
  const isDirty = !!selected && signature(items) !== signature(stored);
  // tagged without a default brand/variant yet: allowed, but not auto-consumed
  const incomplete = items.filter((item) => !isComplete(item));
  const taggedAt = selected ? taggedAtOf(utilizations, selected.id) : undefined;
  const status = STATUS[statusOf(items, products, taggedAt)];
  // saving again acknowledges brands added since the last save
  const hasNewBrands = items.some(
    (item) => newBrandsOf(products, item, taggedAt).length > 0,
  );

  // per-test generics of this phase (or general) not yet tagged.
  // Reusable supplies (ballpen, tourniquet, QC) are dispensed monthly, not per patient.
  const options = useMemo(
    () =>
      GenericProducts.perTest.filter(
        ({ id, phases }) =>
          isUsableIn(phases, phase) &&
          !items.some(({ generic }) => generic.id === id),
      ),
    [items, phase],
  );

  const confirmDiscard = () =>
    !isDirty ||
    window.confirm("You have unsaved changes on this service. Discard them?");

  const handleSelect = (service) => {
    if (service.id === selected?.id || !confirmDiscard()) return;
    setSelected(service);
    setItems(getConfigured(service.id));
  };

  const handlePhase = (key) => {
    if (key === phase || !confirmDiscard()) return;
    const segment = UTILIZATION_PHASES[key].segment;
    const segments = Object.values(UTILIZATION_PHASES).map((p) => p.segment);
    const parts = pathname.split("/");
    if (segments.includes(parts[parts.length - 1]))
      parts[parts.length - 1] = segment;
    else parts.push(segment);
    history.push(
      `${parts.join("/")}?department=${department}${selected ? `&service=${selected.id}` : ""}`,
    );
  };

  const handleDepartment = (key) => {
    if (key === department || !confirmDiscard()) return;
    setDepartment(key);
  };

  const handleAdd = (generic) =>
    setItems((prev) => [
      ...prev,
      { generic, ...autoSelect(products, generic.id), quantity: 1 },
    ]);

  const handleChange = (index, changes) =>
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    );

  const handleRemove = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const { success, payload } = await axioKit.save(
        UTILIZATION_URL,
        {
          branch,
          serviceId: selected.id,
          department: UTILIZATION_DEPARTMENT[department],
          phase,
          items: items.map(({ generic, product, variant, quantity }) => ({
            pgid: generic.id,
            product: product?._id,
            variant: variant?._id,
            quantity,
          })),
        },
        token,
      );
      setUtilizations((prev) => [
        ...prev.filter((u) => u.serviceId !== payload.serviceId),
        payload,
      ]);
      addToast(success, { appearance: "success", autoDismiss: true });
    } catch (error) {
      addToast(error.message, { appearance: "error", autoDismiss: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MDBCard narrow className="pb-2">
      <MDBCardBody>
        {/* header */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="tags" className="mr-2 text-primary" />
              Consumables Tagging
            </h5>
            <small className="text-muted">
              Default brand, variant and units auto-consumed per service.
            </small>
          </div>

          <div
            className="d-flex flex-wrap align-items-center"
            style={{ gap: 8 }}
          >
            <div className="btn-group" role="group" aria-label="Phase">
              {Object.entries(UTILIZATION_PHASES).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm m-0 z-depth-0 ${
                    key === phase ? "btn-primary" : "btn-outline-primary"
                  }`}
                  onClick={() => handlePhase(key)}
                  title={p.title}
                  disabled={isLoading}
                >
                  <MDBIcon icon={p.icon} className="mr-1" />
                  {p.abbr}
                </button>
              ))}
            </div>
            <div className="btn-group" role="group" aria-label="Department">
              {[
                ["LAB", "Laboratory"],
                ["RAD", "Radiology"],
              ].map(([key, text]) => (
                <button
                  key={key}
                  type="button"
                  className={`btn btn-sm m-0 z-depth-0 ${
                    key === department ? "btn-info" : "btn-outline-info"
                  }`}
                  onClick={() => handleDepartment(key)}
                  disabled={isLoading}
                >
                  {text}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* summary */}
        <div className="d-flex flex-wrap mb-3" style={{ gap: 16 }}>
          {Object.entries(STATUS).map(
            ([key, { label: text, icon: statusIcon, color, hint }]) => (
              <span key={key} className="small" title={hint}>
                <MDBIcon icon={statusIcon} className="mr-1" style={{ color }} />
                <b>{counts[key] || 0}</b> {text}
              </span>
            ),
          )}
          <span className="small text-muted ml-auto">
            <MDBIcon icon={icon} className="mr-1" />
            {abbr}: {label}
          </span>
        </div>

        <MDBRow>
          <MDBCol md="4" className="mb-3">
            <ServiceList
              services={services}
              selectedId={selected?.id}
              statusOf={serviceStatus}
              onSelect={handleSelect}
            />
          </MDBCol>

          <MDBCol md="8">
            {!selected ? (
              <div className="border rounded text-center text-muted py-5">
                <MDBIcon icon="hand-point-left" size="2x" className="mb-2" />
                <p className="mb-0">
                  Select a service to tag its {title.toLowerCase()} supplies.
                </p>
                <small>
                  {isLoading
                    ? "Loading..."
                    : "e.g. CBC → EDTA Tube → BD Vacutainer · Tube 3mL × 1"}
                </small>
              </div>
            ) : (
              <div className="border rounded p-3">
                {/* service */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-0 font-weight-bold">
                      {selected.abbreviation &&
                      selected.abbreviation !== selected.name
                        ? `${selected.abbreviation} · ${selected.name}`
                        : selected.name}
                    </h5>
                    <small className="text-muted">
                      {selected.section}
                      {selected.specimen ? ` · ${selected.specimen}` : ""}
                    </small>
                  </div>
                  <div className="text-right">
                    <span
                      className="small font-weight-bold"
                      style={{ color: status.color }}
                      title={status.hint}
                    >
                      <MDBIcon icon={status.icon} className="mr-1" />
                      {status.label}
                    </span>
                    {isDirty && (
                      <small className="d-block text-warning">
                        <MDBIcon icon="pen" className="mr-1" />
                        Unsaved changes
                      </small>
                    )}
                  </div>
                </div>

                {!readOnly && (
                  <SupplyPicker
                    options={options}
                    products={products}
                    onAdd={handleAdd}
                    disabled={isLoading}
                  />
                )}

                <MDBTable small hover responsive className="mt-3 mb-2">
                  <thead>
                    <tr>
                      <th>Generic supply</th>
                      <th style={{ width: "40%" }}>Default brand / variant</th>
                      <th
                        style={{ width: 130 }}
                        title="Default units, can be increased upon dispensing"
                      >
                        Units
                      </th>
                      <th style={{ width: 30 }} />
                      <th style={{ width: 30 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {items.length ? (
                      items.map((item, index) => (
                        <SupplyRow
                          key={item.generic.id}
                          item={item}
                          products={products}
                          taggedAt={taggedAt}
                          onChange={(changes) => handleChange(index, changes)}
                          onRemove={() => handleRemove(index)}
                          disabled={isLoading || readOnly}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          {readOnly
                            ? "No supplies tagged to this service yet."
                            : "No supplies tagged yet. Search above to add one."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </MDBTable>

                {incomplete.length > 0 && (
                  <div className="alert alert-warning py-1 px-2 small mb-2">
                    <MDBIcon icon="exclamation-triangle" className="mr-1" />
                    No default brand/variant yet for{" "}
                    <b>
                      {incomplete
                        .map(({ generic }) => generic.abbreviation)
                        .join(", ")}
                    </b>
                    . They are saved as tagged, but cannot be auto-consumed
                    until a brand is added in Branded Products.
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <MDBIcon icon="info-circle" className="mr-1" />
                    Staff can still change the brand/variant or add units upon
                    dispensing.
                  </small>
                  <div className={readOnly ? "d-none" : ""}>
                    <MDBBtn
                      size="sm"
                      color="light"
                      onClick={() => setItems(saved)}
                      disabled={isLoading || !isDirty}
                    >
                      Reset
                    </MDBBtn>
                    <MDBBtn
                      size="sm"
                      color="primary"
                      className="mr-0"
                      onClick={handleSave}
                      disabled={isLoading || (!isDirty && !hasNewBrands)}
                      title={
                        !isDirty && hasNewBrands
                          ? "Keep the current defaults and mark the new brands as seen"
                          : undefined
                      }
                    >
                      <MDBIcon icon="save" className="mr-1" />
                      {!isDirty && hasNewBrands ? "Keep defaults" : "Save"}
                    </MDBBtn>
                  </div>
                </div>
              </div>
            )}
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
    </MDBCard>
  );
}
